
// Utility functions for generating standardized content based on templates
import { STANDARD_CONTENT_TEMPLATE, COURSE_TYPE_VARIABLES } from '@/data/courses/templates/standardContentTemplate';
import { PRACTICE_EXERCISE_TEMPLATE, EXERCISE_TYPES_BY_TECHNOLOGY } from '@/data/courses/templates/practiceExerciseTemplate';
import { QUIZ_TEMPLATE, QUIZ_PATTERNS_BY_COURSE_TYPE } from '@/data/courses/templates/quizTemplate';

export interface ContentGenerationParams {
  moduleTitle: string;
  courseType: 'aws' | 'java' | 'dotnet' | 'ai' | 'gcp' | 'powerbi' | 'tableau';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  learningObjectives: string[];
  prerequisites: string[];
  keyConceptsData: {
    concept1: { title: string; explanation: string; importance: string; example: string; };
    concept2: { title: string; explanation: string; bestPractices: string[]; };
    concept3: { title: string; explanation: string; };
  };
  practicalExamples: {
    example1: { title: string; description: string; code: string; explanation: string; };
    example2: { title: string; description: string; code: string; keyPoints: string[]; };
  };
  industryContext: {
    usage: string;
    tools: { name: string; description: string; }[];
    careerRelevance: string;
  };
  takeaways: string[];
  pitfalls: string[];
  nextSteps: string[];
}

export const generateStandardizedContent = (params: ContentGenerationParams): string => {
  const courseVars = COURSE_TYPE_VARIABLES[params.courseType] || COURSE_TYPE_VARIABLES.java;
  
  let content = STANDARD_CONTENT_TEMPLATE.moduleIntroduction
    .replace('{MODULE_TITLE}', params.moduleTitle)
    .replace('{OBJECTIVE_1}', params.learningObjectives[0] || 'Understand core concepts')
    .replace('{OBJECTIVE_2}', params.learningObjectives[1] || 'Apply practical techniques')
    .replace('{OBJECTIVE_3}', params.learningObjectives[2] || 'Implement best practices')
    .replace('{OBJECTIVE_4}', params.learningObjectives[3] || 'Solve real-world problems')
    .replace('{PREREQUISITE_1}', params.prerequisites[0] || 'Basic understanding of programming')
    .replace('{PREREQUISITE_2}', params.prerequisites[1] || 'Familiarity with development tools')
    .replace('{MODULE_OVERVIEW_DESCRIPTION}', `This module explores ${params.moduleTitle.toLowerCase()} within the context of ${courseVars.contextFocus}.`)
    .replace('{ESTIMATED_TIME}', params.estimatedTime)
    .replace('{DIFFICULTY_LEVEL}', params.difficulty);

  content += STANDARD_CONTENT_TEMPLATE.coreContentStructure
    .replace('{CONCEPT_1_TITLE}', params.keyConceptsData.concept1.title)
    .replace('{CONCEPT_1_EXPLANATION}', params.keyConceptsData.concept1.explanation)
    .replace('{CONCEPT_1_IMPORTANCE}', params.keyConceptsData.concept1.importance)
    .replace('{CONCEPT_1_REAL_WORLD_EXAMPLE}', params.keyConceptsData.concept1.example)
    .replace('{CONCEPT_2_TITLE}', params.keyConceptsData.concept2.title)
    .replace('{CONCEPT_2_EXPLANATION}', params.keyConceptsData.concept2.explanation)
    .replace('{BEST_PRACTICE_1}', params.keyConceptsData.concept2.bestPractices[0] || 'Follow industry standards')
    .replace('{BEST_PRACTICE_2}', params.keyConceptsData.concept2.bestPractices[1] || 'Write clean, maintainable code')
    .replace('{BEST_PRACTICE_3}', params.keyConceptsData.concept2.bestPractices[2] || 'Document your work clearly')
    .replace('{CONCEPT_3_TITLE}', params.keyConceptsData.concept3.title)
    .replace('{CONCEPT_3_EXPLANATION}', params.keyConceptsData.concept3.explanation);

  content += STANDARD_CONTENT_TEMPLATE.practicalExamples
    .replace('{EXAMPLE_1_TITLE}', params.practicalExamples.example1.title)
    .replace('{EXAMPLE_1_DESCRIPTION}', params.practicalExamples.example1.description)
    .replace('{LANGUAGE}', courseVars.language)
    .replace('{EXAMPLE_1_CODE}', params.practicalExamples.example1.code)
    .replace('{EXAMPLE_1_EXPLANATION}', params.practicalExamples.example1.explanation)
    .replace('{EXAMPLE_2_TITLE}', params.practicalExamples.example2.title)
    .replace('{EXAMPLE_2_DESCRIPTION}', params.practicalExamples.example2.description)
    .replace('{EXAMPLE_2_CODE}', params.practicalExamples.example2.code)
    .replace('{KEY_POINT_1}', params.practicalExamples.example2.keyPoints[0] || 'Key implementation detail')
    .replace('{KEY_POINT_2}', params.practicalExamples.example2.keyPoints[1] || 'Important consideration');

  content += STANDARD_CONTENT_TEMPLATE.industryContext
    .replace('{INDUSTRY_USAGE_DESCRIPTION}', params.industryContext.usage)
    .replace('{TOOL_1}', params.industryContext.tools[0]?.name || 'Industry Tool 1')
    .replace('{TOOL_1_DESCRIPTION}', params.industryContext.tools[0]?.description || 'Professional development tool')
    .replace('{TOOL_2}', params.industryContext.tools[1]?.name || 'Industry Tool 2')
    .replace('{TOOL_2_DESCRIPTION}', params.industryContext.tools[1]?.description || 'Widely used framework')
    .replace('{TOOL_3}', params.industryContext.tools[2]?.name || 'Industry Tool 3')
    .replace('{TOOL_3_DESCRIPTION}', params.industryContext.tools[2]?.description || 'Standard development practice')
    .replace('{CAREER_RELEVANCE_DESCRIPTION}', params.industryContext.careerRelevance);

  content += STANDARD_CONTENT_TEMPLATE.moduleSummary
    .replace('{TAKEAWAY_1}', params.takeaways[0] || 'Core concept mastery')
    .replace('{TAKEAWAY_2}', params.takeaways[1] || 'Practical application skills')
    .replace('{TAKEAWAY_3}', params.takeaways[2] || 'Best practice implementation')
    .replace('{PITFALL_1}', params.pitfalls[0] || 'Common implementation mistakes')
    .replace('{PITFALL_2}', params.pitfalls[1] || 'Performance considerations')
    .replace('{NEXT_STEP_1}', params.nextSteps[0] || 'Complete practice exercises')
    .replace('{NEXT_STEP_2}', params.nextSteps[1] || 'Take the knowledge quiz');

  return content;
};

export const generatePracticeExercise = (
  exerciseId: string,
  title: string,
  courseType: keyof typeof EXERCISE_TYPES_BY_TECHNOLOGY,
  difficulty: 'beginner' | 'intermediate' | 'advanced',
  customParams: {
    description: string;
    requirements: string[];
    starterCode: string;
    solutionCode: string;
    hints: string[];
    explanation: string;
  }
) => {
  const template = { ...PRACTICE_EXERCISE_TEMPLATE.basicExercise };
  
  return {
    ...template,
    id: exerciseId,
    title: title,
    description: customParams.description,
    difficulty: difficulty,
    estimatedTime: difficulty === 'beginner' ? '15-20 minutes' : difficulty === 'intermediate' ? '25-35 minutes' : '40-60 minutes',
    task: template.task
      .replace('{TASK_DESCRIPTION}', customParams.description)
      .replace('{REQUIREMENT_1}', customParams.requirements[0] || 'Implement core functionality')
      .replace('{REQUIREMENT_2}', customParams.requirements[1] || 'Follow best practices')
      .replace('{REQUIREMENT_3}', customParams.requirements[2] || 'Write clean, readable code')
      .replace('{SUCCESS_CRITERIA_1}', 'Code runs without errors')
      .replace('{SUCCESS_CRITERIA_2}', 'Meets all specified requirements'),
    starter: customParams.starterCode,
    solution: customParams.solutionCode,
    hints: customParams.hints,
    explanation: template.explanation
      .replace('{SOLUTION_EXPLANATION}', customParams.explanation)
      .replace('{CONCEPT_1}', 'Core implementation pattern')
      .replace('{CONCEPT_2}', 'Best practice application')
      .replace('{APPROACH_EXPLANATION}', 'This solution demonstrates proper implementation while following industry standards.')
  };
};

export const generateModuleQuiz = (
  moduleId: string,
  moduleTitle: string,
  courseType: keyof typeof QUIZ_PATTERNS_BY_COURSE_TYPE,
  customQuestions: any[]
) => {
  const template = { ...QUIZ_TEMPLATE.standardQuiz };
  
  return {
    ...template,
    moduleId: moduleId,
    title: `${moduleTitle} - Knowledge Check`,
    questions: customQuestions.map((q, index) => ({
      ...QUIZ_TEMPLATE.questionTemplates.multipleChoice,
      id: `${moduleId}-q${index + 1}`,
      ...q
    }))
  };
};

// Validation function for generated content
export const validateGeneratedContent = (content: string): {
  isValid: boolean;
  issues: string[];
  score: number;
} => {
  const issues: string[] = [];
  let score = 100;

  if (content.length < 800) {
    issues.push('Content is too short (minimum 800 characters)');
    score -= 20;
  }

  if (!content.includes('## Learning Objectives')) {
    issues.push('Missing learning objectives section');
    score -= 15;
  }

  if (!content.includes('## Key Concepts')) {
    issues.push('Missing key concepts section');
    score -= 15;
  }

  if (!content.includes('## Hands-On Examples')) {
    issues.push('Missing practical examples section');
    score -= 20;
  }

  if (!content.includes('## Key Takeaways')) {
    issues.push('Missing summary section');
    score -= 10;
  }

  const codeBlockCount = (content.match(/```/g) || []).length / 2;
  if (codeBlockCount < 2) {
    issues.push('Insufficient code examples (minimum 2)');
    score -= 10;
  }

  return {
    isValid: issues.length === 0,
    issues,
    score: Math.max(0, score)
  };
};
